import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, type BreakingItem, type Locale } from '@nk/shared';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { usePageHidden, useTitleAlert } from '@/lib/dom';
import { useAfterLoad } from '@/lib/hooks';
import { POLL_INTERVAL_MS, useEventStream, type StreamStatus } from '@/lib/use-event-stream';

/**
 * The ticker's data — PRD §5, phases.md Phase 11.
 *
 * Three things happen here that the ticker component should not have to know about:
 *
 * 1. **First load over HTTP, changes over SSE.** The page arrives with the current items
 *    (server-rendered, §1.10), and `/stream/breaking` sends the whole replacement list on
 *    every change — so a client that missed an event still converges on the right state,
 *    which a stream of deltas could not promise.
 * 2. **Items expire on the client.** An item with an `endTime` disappears at that
 *    instant, without waiting for a server push: the editor scheduled the end, and a
 *    reader whose tab has been open since before it should not still be reading it.
 * 3. **The tab title carries the count** while the reader is in another tab — the one
 *    piece of §5 that has to reach someone who is not looking at the page.
 *
 * When the stream gives up (`polling`), a 30-second refetch takes over (§1.8).
 */
export function useBreaking(locale: Locale): {
  items: BreakingItem[];
  status: StreamStatus;
  isPending: boolean;
} {
  const client = useQueryClient();
  const key = queryKeys.breaking(locale);

  const { data, refetch, isPending } = useQuery({
    queryKey: key,
    queryFn: () => publicApi.breaking(locale),
    staleTime: STALE.realtime,
  });

  const onEvent = useCallback(
    ({ data: payload }: { data: unknown }) => {
      const event = payload as { type?: string; language?: Locale; items?: BreakingItem[] };
      // The channel is sitewide, so an Assamese reader hears about English changes too.
      if (event.type !== 'breaking.changed' || event.language !== locale) return;
      if (!Array.isArray(event.items)) return;
      client.setQueryData(key, event.items);
    },
    // `key` is a fresh array each render; the locale is what actually identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, locale],
  );

  /**
   * Connected after the page has settled, not during hydration (Phase 17). The ticker
   * arrives server-rendered with the current items, so the only thing the delay costs
   * is how quickly a *change* is picked up — against a high-priority connection held
   * open through the whole first paint on a slow link.
   */
  const { status } = useEventStream({
    url: '/api/v1/stream/breaking',
    onEvent,
    enabled: useAfterLoad(),
  });

  // §1.8's fallback: when the stream has given up, ask the ordinary endpoint instead.
  // The callback goes through a ref so that a re-render — the clock in the ticker ticks
  // every second — cannot restart the interval and postpone the poll forever.
  const poll = useRef(refetch);
  poll.current = refetch;
  useEffect(() => {
    if (status !== 'polling') return undefined;
    const timer = setInterval(() => void poll.current(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status]);

  const items = useUnexpired(data ?? []);
  useTitleAlert(useUnseenCount(items));

  return { items, status, isPending };
}

/**
 * Drops items whose `endTime` has passed, and re-runs itself exactly when the next one
 * expires rather than on an interval — a timer per second to notice something that
 * happens twice a day is a page that never idles.
 */
function useUnexpired(items: BreakingItem[]): BreakingItem[] {
  const [now, setNow] = useState(() => Date.now());

  const live = useMemo(
    () =>
      items
        .filter((item) => !item.endTime || new Date(item.endTime).getTime() > now)
        // Priority is the editor's ordering (PRD §5); the API sorts, and so does this,
        // because an item spliced in from the stream has to land in the right place.
        .sort((a, b) => b.priority - a.priority || a.id - b.id),
    [items, now],
  );

  const nextExpiry = useMemo(() => {
    const times = items
      .map((item) => (item.endTime ? new Date(item.endTime).getTime() : Number.POSITIVE_INFINITY))
      .filter((time) => time > now);
    return times.length > 0 ? Math.min(...times) : null;
  }, [items, now]);

  useEffect(() => {
    if (nextExpiry === null || !Number.isFinite(nextExpiry)) return undefined;
    // `setTimeout` saturates above ~24.8 days; anything that far out can wait for a
    // reload. The `+50` lands the tick just past the boundary rather than on it.
    const delay = Math.min(nextExpiry - Date.now() + 50, 2 ** 31 - 1);
    const timer = setTimeout(() => setNow(Date.now()), Math.max(delay, 0));
    return () => clearTimeout(timer);
  }, [nextExpiry]);

  return live;
}

/**
 * How many items arrived while the reader was in another tab.
 *
 * Counted by id rather than by length: the ticker is a replacement list, so an editor
 * who removes one item and adds two has not "sent one update", and a reader coming back
 * to `(1)` when two stories broke is being told something false.
 */
function useUnseenCount(items: BreakingItem[]): number {
  const hidden = usePageHidden();
  const seen = useRef<Set<number>>(new Set());
  const [unseen, setUnseen] = useState(0);

  useEffect(() => {
    if (!hidden) {
      // Back in view: everything on screen counts as seen, and the title drops its badge.
      seen.current = new Set(items.map((item) => item.id));
      setUnseen(0);
      return;
    }
    const fresh = items.filter((item) => !seen.current.has(item.id)).length;
    if (fresh > 0) setUnseen(fresh);
  }, [items, hidden]);

  return unseen;
}
